import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { format, parseISO, startOfISOWeek } from 'date-fns';
import { db } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import {
  Loader2, Activity, Search, Clock, UserCheck, UserPlus, Link2Off, Server, Calendar, Circle,
  Users, TrendingUp, Flame, CalendarDays, CalendarRange, BarChart3, FileSearch, Wand2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  searchUsersByNickname, admin_linkPlayerAccount, admin_autoLinkPlayers,
  type AutoLinkMatch, type AutoLinkAmbiguous,
} from '@/lib/actions';
import { cn } from '@/lib/utils';

type PlayerActivity = {
  id: string;
  name?: string;
  uuid?: string;
  totalSeconds?: number;
  days?: Record<string, number>;
  linkedUid?: string;
  linkedNickname?: string;
  firstSeen?: any;
  lastSeen?: any;
  lastServerName?: string;
};

// Agregado diario que escribe el latido del mod (`mod_activity_daily/{yyyy-mm-dd}`).
type DailyStat = {
  id: string;
  date?: string;
  players?: Record<string, boolean>;
  hourPlayers?: Record<string, Record<string, boolean>>;
  hourPeak?: Record<string, number>;
  peakPlayers?: number;
  peakHour?: string;
};

function tsMillis(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  const t = new Date(ts).getTime();
  return isNaN(t) ? 0 : t;
}

function fmtDuration(sec: number) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function fmtDate(ts: any) {
  const ms = tsMillis(ts);
  return ms ? new Date(ms).toLocaleString('es-ES') : '—';
}

function fmtHours(sec: number) {
  const h = (Math.max(0, sec || 0)) / 3600;
  if (h >= 10) return `${Math.round(h)} h`;
  return `${h.toFixed(1)} h`;
}

function fmtDayLabel(date: string) {
  try { return format(parseISO(date), "d 'de' MMM"); } catch { return date; }
}

// Fecha y segundos transcurridos del día en hora de Madrid, que es la zona con la
// que el latido del mod indexa los días (`days`, `mod_activity_daily`).
function madridNowParts() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(new Date());
  const get = (t: string) => (parts.find((p) => p.type === t)?.value || '0');
  let hour = parseInt(get('hour'), 10);
  if (hour === 24) hour = 0;
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    elapsed: hour * 3600 + parseInt(get('minute'), 10) * 60 + parseInt(get('second'), 10),
  };
}

const DAY_SECONDS = 24 * 3600;

const ONLINE_MS = 60 * 1000;

// ── Tarjeta de titular (número grande; sin gráfica: el dato es un solo valor) ──
function StatTile({ icon, label, value, hint, color }: { icon: ReactNode; label: string; value: string; hint?: string; color: string }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
      <p className="flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-wide text-muted-foreground">
        <span style={{ color }}>{icon}</span>{label}
      </p>
      <p className="mt-1.5 text-3xl font-black tabular-nums" style={{ color }}>{value}</p>
      <p className="mt-0.5 h-4 truncate text-xs text-muted-foreground">{hint || ''}</p>
    </div>
  );
}

// ── Barras verticales de una sola serie (magnitud → un solo tono) ──
function BarChart({
  bars, accent, emptyLabel,
}: {
  bars: { key: string; label: string; value: number; tooltip: string }[];
  accent: string;
  emptyLabel: string;
}) {
  const max = Math.max(1, ...bars.map((b) => b.value));
  const hasData = bars.some((b) => b.value > 0);
  if (!hasData) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="flex h-44 items-end gap-[2px]">
      {bars.map((b) => (
        <div key={b.key} className="group relative flex h-full flex-1 flex-col justify-end">
          {/* tooltip al pasar el ratón */}
          <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-white/10 bg-popover px-2 py-1 text-[0.7rem] shadow-xl group-hover:block">
            {b.tooltip}
          </div>
          <div className="w-full rounded-t-[4px] transition-opacity group-hover:opacity-80"
            style={{ height: `${b.value > 0 ? Math.max(3, (b.value / max) * 100) : 0}%`, background: accent, minHeight: b.value > 0 ? 3 : 0 }} />
          <div className="mt-1 h-px w-full bg-white/10" />
          <span className="mt-1 block truncate text-center text-[0.6rem] tabular-nums text-muted-foreground">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Buscador de cuenta para vincular ──
function AccountLinker({ activityId, onDone }: { activityId: string; onDone: () => void }) {
  const { toast } = useToast();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<{ id: string; nickname: string; profileImageUrl: string | null }[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { users } = await searchUsersByNickname(q);
      setResults(users || []);
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const assign = async (uid: string, nickname: string) => {
    setBusy(uid);
    const res = await admin_linkPlayerAccount(activityId, uid);
    toast({ title: res.success ? 'Vinculado' : 'Error', description: res.success ? `Cuenta: ${nickname}` : res.message, variant: res.success ? undefined : 'destructive' });
    setBusy(null);
    if (res.success) onDone();
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar usuario de Jartinet..." className="pl-9" />
        {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>
      {results.length > 0 && (
        <div className="space-y-1">
          {results.map((u) => (
            <div key={u.id} className="flex items-center gap-2 rounded-xl border border-white/8 bg-muted/30 p-2">
              <Avatar className="h-8 w-8"><AvatarImage src={u.profileImageUrl || undefined} /><AvatarFallback>{u.nickname?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
              <span className="flex-1 truncate text-sm">{u.nickname}</span>
              <Button size="sm" variant="secondary" disabled={busy === u.id} onClick={() => assign(u.id, u.nickname)}>
                {busy === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Vincular'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detalle de un jugador ──
function PlayerDialog({ player, onClose }: { player: PlayerActivity; onClose: () => void }) {
  const { toast } = useToast();
  const [unlinking, setUnlinking] = useState(false);

  const days = useMemo(() => {
    const d = player.days || {};
    return Object.entries(d).map(([date, sec]) => ({ date, sec: Number(sec) || 0 })).sort((a, b) => b.date.localeCompare(a.date));
  }, [player.days]);
  const maxDay = days.reduce((m, d) => Math.max(m, d.sec), 0) || 1;

  const unlink = async () => {
    setUnlinking(true);
    const res = await admin_linkPlayerAccount(player.id, null);
    toast({ title: res.success ? 'Desvinculado' : 'Error', description: res.message, variant: res.success ? undefined : 'destructive' });
    setUnlinking(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="flex items-center gap-2">{player.name || player.uuid || 'Jugador'}</DialogTitle>
          <DialogDescription>Tiempo total en el servidor y actividad por día.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 pb-6">
          {/* Resumen */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Tiempo total</p>
              <p className="mt-1 text-2xl font-black text-primary">{fmtDuration(player.totalSeconds || 0)}</p>
            </div>
            <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground"><Server className="h-3.5 w-3.5" /> Último server</p>
              <p className="mt-1 truncate text-sm font-semibold">{player.lastServerName || '—'}</p>
              <p className="text-xs text-muted-foreground">Visto: {fmtDate(player.lastSeen)}</p>
            </div>
          </div>

          {/* Cuenta vinculada */}
          <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><UserCheck className="h-4 w-4 text-emerald-400" /> Cuenta de Jartinet</p>
            {player.linkedUid ? (
              <div className="flex items-center justify-between gap-2">
                <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-sm text-emerald-300">{player.linkedNickname || player.linkedUid}</span>
                <Button size="sm" variant="ghost" className="gap-1 text-rose-400" disabled={unlinking} onClick={unlink}>
                  {unlinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2Off className="h-4 w-4" />} Desvincular
                </Button>
              </div>
            ) : (
              <>
                <p className="mb-2 text-xs text-muted-foreground">Sin cuenta asignada. Busca y vincula un usuario:</p>
                <AccountLinker activityId={player.id} onDone={() => { /* snapshot en vivo actualiza */ }} />
              </>
            )}
          </div>

          {/* Actividad por día */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Calendar className="h-4 w-4 text-sky-400" /> Actividad por día</p>
            {days.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin actividad registrada aún.</p>
            ) : (
              <div className="space-y-1.5">
                {days.map((d) => (
                  <div key={d.date} className="flex items-center gap-3">
                    <span className="w-24 shrink-0 text-xs text-muted-foreground">{d.date}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
                      <div className="h-full rounded-full bg-gradient-to-r from-primary/70 to-primary" style={{ width: `${Math.max(4, (d.sec / maxDay) * 100)}%` }} />
                    </div>
                    <span className="w-16 shrink-0 text-right text-xs font-semibold">{fmtDuration(d.sec)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-center text-xs text-muted-foreground">Primera vez: {fmtDate(player.firstSeen)}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminActividadPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<PlayerActivity | null>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  const activityQuery = useMemo(() => query(collection(db, 'mod_player_activity'), orderBy('totalSeconds', 'desc')), []);
  const { data: rawPlayers, error } = useCollection<PlayerActivity>(activityQuery);

  const dailyQuery = useMemo(() => query(collection(db, 'mod_activity_daily'), orderBy('date', 'desc')), []);
  const { data: rawDaily } = useCollection<DailyStat>(dailyQuery);

  useEffect(() => { if (error) toast({ title: 'Error', description: 'No se pudo cargar la actividad.', variant: 'destructive' }); }, [error, toast]);

  // Conexión automática desde los formularios: al abrir la página se busca el
  // nombre de Minecraft de cada jugador sin cuenta en `player_applications` (y
  // en el perfil) y se le vincula su cuenta de Jartinet. Los nombres con varias
  // cuentas posibles no se tocan: se listan para vincularlos a mano.
  const [autoLink, setAutoLink] = useState<{ matches: AutoLinkMatch[]; ambiguous: AutoLinkAmbiguous[]; error?: string } | null>(null);
  const [autoLinkBusy, setAutoLinkBusy] = useState(false);
  const runAutoLink = async () => {
    setAutoLinkBusy(true);
    const res = await admin_autoLinkPlayers(true);
    setAutoLink({ matches: res.matches, ambiguous: res.ambiguous, error: res.success ? undefined : res.message });
    setAutoLinkBusy(false);
  };
  const autoLinkRan = useRef(false);
  useEffect(() => {
    if (loading || userData?.role !== 'admin' || autoLinkRan.current) return;
    autoLinkRan.current = true;
    runAutoLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, userData?.role]);
  const unlinkedCount = useMemo(() => (rawPlayers || []).filter((p) => !(p as PlayerActivity).linkedUid).length, [rawPlayers]);

  // Estadísticas globales derivadas del desglose por día de cada jugador
  // (`days`), así cubren todo el histórico ya registrado.
  const stats = useMemo(() => {
    const list = (rawPlayers || []) as PlayerActivity[];
    const secondsByDate = new Map<string, number>();
    const playersByDate = new Map<string, number>();

    // Para las medias POR JUGADOR: se agrupa por pareja jugador+periodo, así la
    // media es "lo que juega una persona", no el total repartido entre días.
    const playerDaySecs: number[] = [];
    const byPlayerWeek = new Map<string, number>();
    const byPlayerMonth = new Map<string, number>();
    const weekKeyOf = (d: string) => { try { return format(startOfISOWeek(parseISO(d)), 'yyyy-MM-dd'); } catch { return d; } };

    list.forEach((p) => {
      Object.entries(p.days || {}).forEach(([date, sec]) => {
        const s = Number(sec) || 0;
        if (s <= 0) return;
        secondsByDate.set(date, (secondsByDate.get(date) || 0) + s);
        playersByDate.set(date, (playersByDate.get(date) || 0) + 1);
        playerDaySecs.push(s);
        const wk = `${p.id}|${weekKeyOf(date)}`;
        byPlayerWeek.set(wk, (byPlayerWeek.get(wk) || 0) + s);
        const mo = `${p.id}|${date.slice(0, 7)}`;
        byPlayerMonth.set(mo, (byPlayerMonth.get(mo) || 0) + s);
      });
    });

    const dates = [...secondsByDate.keys()].sort();
    const numDays = dates.length;

    const mean = (vals: number[]) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0);

    // Media de jugadores SIMULTÁNEOS de un día = tiempo jugado entre todos ÷ duración
    // del día (media ponderada en el tiempo). Se puede derivar del histórico; el pico
    // real de concurrencia no (hace falta muestrear, ver `mod_activity_daily`).
    const today = madridNowParts();
    const spanOf = (d: string) => (d === today.date ? Math.max(today.elapsed, 3600) : DAY_SECONDS);
    const concurrencyByDate = new Map<string, number>();
    secondsByDate.forEach((s, d) => concurrencyByDate.set(d, s / spanOf(d)));

    return {
      totalPlayers: list.length,
      numDays,
      avgConcurrent: mean([...concurrencyByDate.values()]),
      // Medias por jugador, contando solo a quien jugó en ese día/semana/mes.
      avgSecPerPlayerDay: mean(playerDaySecs),
      avgSecPerPlayerWeek: mean([...byPlayerWeek.values()]),
      avgSecPerPlayerMonth: mean([...byPlayerMonth.values()]),
      dailySeries: dates.slice(-30).map((d) => ({
        date: d,
        players: playersByDate.get(d) || 0,
        sec: secondsByDate.get(d) || 0,
        avgConcurrent: concurrencyByDate.get(d) || 0,
      })),
    };
  }, [rawPlayers]);

  // Día seleccionado para el desglose por horas (por defecto, el más reciente).
  const dailyDocs = useMemo(() => (rawDaily || []) as DailyStat[], [rawDaily]);
  const [hourDay, setHourDay] = useState<string | null>(null);
  const activeDay = useMemo(
    () => dailyDocs.find((d) => d.id === hourDay) || dailyDocs[0] || null,
    [dailyDocs, hourDay],
  );

  // Pico de concurrencia por día: lo muestrea el latido (`peakPlayers`), no se puede
  // reconstruir del histórico → solo hay datos desde que se desplegó.
  const peakByDate = useMemo(() => {
    const m = new Map<string, number>();
    dailyDocs.forEach((d) => m.set(d.date || d.id, Number(d.peakPlayers || 0)));
    return m;
  }, [dailyDocs]);

  const peakRecord = useMemo(() => {
    let best = { date: '', count: 0 };
    peakByDate.forEach((count, date) => { if (count > best.count) best = { date, count }; });
    return best;
  }, [peakByDate]);

  const dailyBars = useMemo(() => stats.dailySeries.map((d) => {
    const peak = peakByDate.get(d.date) || 0;
    return {
      key: d.date,
      label: d.date.slice(8, 10),
      value: d.avgConcurrent,
      tooltip: `${fmtDayLabel(d.date)} · ${d.avgConcurrent.toFixed(1)} de media${peak ? ` · pico ${peak}` : ''} · ${d.players} jugador${d.players === 1 ? '' : 'es'} distinto${d.players === 1 ? '' : 's'} · ${fmtHours(d.sec)}`,
    };
  }), [stats.dailySeries, peakByDate]);

  const hourBars = useMemo(() => {
    const day = activeDay;
    return Array.from({ length: 24 }, (_, h) => {
      const k = String(h).padStart(2, '0');
      const distinct = Object.keys(day?.hourPlayers?.[k] || {}).length;
      const peak = Number(day?.hourPeak?.[k] || 0);
      return {
        key: k,
        label: h % 3 === 0 ? k : '',
        value: peak,
        tooltip: `${k}:00 · ${peak} a la vez${distinct !== peak ? ` · ${distinct} distinto${distinct === 1 ? '' : 's'}` : ''}`,
      };
    });
  }, [activeDay]);

  const players = useMemo(() => {
    const list = (rawPlayers || []) as PlayerActivity[];
    const q = search.trim().toLowerCase();
    const filtered = q ? list.filter((p) => `${p.name} ${p.linkedNickname} ${p.uuid}`.toLowerCase().includes(q)) : list;
    return filtered;
  }, [rawPlayers, search]);

  // Mantén sincronizado el jugador seleccionado con el snapshot en vivo.
  const selectedLive = useMemo(
    () => (selected ? (rawPlayers || []).find((p) => p.id === selected.id) || selected : null),
    [selected, rawPlayers],
  );

  const now = Date.now();

  if (loading || userData?.role !== 'admin') {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 pb-12">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-emerald-500/15 via-card/40 to-card/20 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl border border-emerald-400/30 bg-emerald-500/15 text-emerald-300"><Activity className="h-9 w-9" /></div>
          <div>
            <h1 className="font-headline text-4xl font-bold md:text-5xl">Actividad del Servidor</h1>
            <p className="mt-2 text-muted-foreground">Jugadores que han entrado al servidor, su tiempo total y su cuenta de Jartinet.</p>
          </div>
        </div>
      </motion.div>

      {/* ── Estadísticas del servidor ── */}
      <section className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
          <StatTile icon={<Users className="h-3.5 w-3.5" />} label="Jugadores totales" color="#10b981"
            value={String(stats.totalPlayers)} hint="han entrado alguna vez" />
          <StatTile icon={<TrendingUp className="h-3.5 w-3.5" />} label="Media simultáneos / día" color="#38bdf8"
            value={stats.avgConcurrent.toFixed(1)} hint={stats.numDays ? `a la vez, media de las 24 h` : 'sin datos aún'} />
          <StatTile icon={<Flame className="h-3.5 w-3.5" />} label="Récord simultáneos" color="#fb923c"
            value={peakRecord.count ? String(peakRecord.count) : '—'}
            hint={peakRecord.date ? `a la vez · ${fmtDayLabel(peakRecord.date)}` : 'se mide desde hoy'} />
          <StatTile icon={<Clock className="h-3.5 w-3.5" />} label="Horas por jugador / día" color="#fbbf24"
            value={fmtHours(stats.avgSecPerPlayerDay)} hint="el día que juega" />
          <StatTile icon={<CalendarDays className="h-3.5 w-3.5" />} label="Horas por jugador / semana" color="#a78bfa"
            value={fmtHours(stats.avgSecPerPlayerWeek)} hint="la semana que juega" />
          <StatTile icon={<CalendarRange className="h-3.5 w-3.5" />} label="Horas por jugador / mes" color="#f472b6"
            value={fmtHours(stats.avgSecPerPlayerMonth)} hint="el mes que juega" />
        </div>

        {/* Jugadores por día */}
        <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
          <p className="mb-4 flex items-center gap-1.5 text-sm font-semibold">
            <BarChart3 className="h-4 w-4 text-sky-400" /> Jugadores simultáneos por día
            <span className="font-normal text-muted-foreground">· media del día · últimos 30 días con actividad</span>
          </p>
          <BarChart bars={dailyBars} accent="#38bdf8" emptyLabel="Todavía no hay actividad registrada." />
        </div>

        {/* Jugadores por hora */}
        <div className="rounded-2xl border border-white/8 bg-card/40 p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-1.5 text-sm font-semibold">
              <Clock className="h-4 w-4 text-emerald-400" /> Jugadores simultáneos por hora
              {activeDay?.date && <span className="font-normal text-muted-foreground">· {fmtDayLabel(activeDay.date)}</span>}
            </p>
            <div className="flex items-center gap-3">
              {!!activeDay?.peakPlayers && (
                <span className="rounded-full bg-orange-500/15 px-3 py-1 text-xs text-orange-300">
                  Pico: {activeDay.peakPlayers} a la vez{activeDay.peakHour ? ` (${activeDay.peakHour}:00)` : ''}
                </span>
              )}
              {dailyDocs.length > 0 && (
                <select
                  value={activeDay?.id || ''}
                  onChange={(e) => setHourDay(e.target.value)}
                  className="rounded-xl border border-white/10 bg-muted/40 px-3 py-1.5 text-xs"
                >
                  {dailyDocs.map((d) => (
                    <option key={d.id} value={d.id}>{fmtDayLabel(d.date || d.id)}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <BarChart bars={hourBars} accent="#10b981"
            emptyLabel="Sin desglose por horas todavía. Se registra a partir de ahora, con cada latido del servidor." />
        </div>
      </section>

      {/* ── Cuentas conectadas desde los formularios ── */}
      <div className="rounded-2xl border border-sky-400/30 bg-sky-500/5 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <FileSearch className="h-4 w-4 text-sky-400" /> Cuentas conectadas desde los formularios
          </p>
          <Button size="sm" variant="secondary" disabled={autoLinkBusy} onClick={runAutoLink} className="gap-1.5">
            {autoLinkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Volver a buscar
          </Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Al abrir esta página, el nombre de Minecraft de cada jugador sin cuenta se busca en los formularios de acceso
          (y en los perfiles de Jartinet) y se le conecta su cuenta automáticamente. Ahora mismo hay{' '}
          <strong className="text-foreground">{unlinkedCount}</strong> jugador{unlinkedCount === 1 ? '' : 'es'} sin cuenta.
        </p>
        {autoLinkBusy && !autoLink ? (
          <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Cruzando con los formularios…</p>
        ) : autoLink?.error ? (
          <p className="mt-3 text-xs text-rose-300">{autoLink.error}</p>
        ) : autoLink ? (
          <div className="mt-3 space-y-3">
            {autoLink.matches.length === 0 ? (
              <p className="text-xs text-muted-foreground">No había jugadores sin cuenta que se pudieran conectar por formulario.</p>
            ) : (
              <div>
                <p className="mb-2 text-xs font-semibold text-emerald-300">
                  {autoLink.matches.length} cuenta{autoLink.matches.length === 1 ? '' : 's'} conectada{autoLink.matches.length === 1 ? '' : 's'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {autoLink.matches.map((m) => (
                    <span key={m.activityId} className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs text-emerald-200">
                      {m.name} → <strong>{m.nickname}</strong>
                      <span className="opacity-60"> · {m.source === 'mc_uuid' ? 'vinculación del mod' : m.source}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {autoLink.ambiguous.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold text-amber-300">Con varias cuentas posibles (vincúlalos a mano):</p>
                <div className="space-y-1">
                  {autoLink.ambiguous.map((a) => (
                    <p key={a.activityId} className="text-xs text-amber-200/90">
                      <strong>{a.name}</strong>: {a.uids.map((u) => u.nickname).join(', ')}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar jugador o cuenta..." className="pl-9" />
      </div>

      {players.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/8 py-16 text-muted-foreground">
          <Activity className="mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">Todavía no hay jugadores registrados{search ? ' que coincidan' : ''}. Aparecen cuando entran al servidor con el mod.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {players.map((p) => {
            const online = now - tsMillis(p.lastSeen) < ONLINE_MS;
            return (
              <button key={p.id} onClick={() => setSelected(p)} className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4 text-left transition-colors hover:border-emerald-400/40">
                <div className="relative">
                  <Avatar className="h-10 w-10 border border-white/10">
                    <AvatarImage src={p.uuid ? `https://mc-heads.net/avatar/${p.uuid}/40` : undefined} />
                    <AvatarFallback>{(p.name || '?').charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  {online && <Circle className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 fill-emerald-500 text-emerald-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate font-semibold">
                    {p.name || p.uuid || 'Jugador'}
                    {online && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.65rem] font-semibold text-emerald-300">En línea</span>}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {p.linkedUid
                      ? <span className="inline-flex items-center gap-1 text-emerald-300"><UserCheck className="h-3 w-3" /> {p.linkedNickname || 'Cuenta vinculada'}</span>
                      : <span className="inline-flex items-center gap-1"><UserPlus className="h-3 w-3" /> Sin cuenta</span>}
                    {' · '}visto {fmtDate(p.lastSeen)}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="flex items-center justify-end gap-1 text-lg font-black text-primary"><Clock className="h-4 w-4" /> {fmtDuration(p.totalSeconds || 0)}</p>
                  <p className="text-[0.7rem] text-muted-foreground">tiempo total</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selectedLive && <PlayerDialog player={selectedLive} onClose={() => setSelected(null)} />}
    </div>
  );
}
